import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { X, Send, Loader2, Minus, Maximize2, Mic, MicOff, Calculator, HelpCircle, Calendar, Compass, BookOpen } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Renders a TED reply with clickable URLs and **bold** markdown.
// Splits on URLs first, then on **bold** within each plain segment.
const URL_RE = /(https?:\/\/[^\s)]+)/g;
const BOLD_RE = /\*\*([^*]+)\*\*/g;
const renderRichSegment = (segment, baseKey) => {
  const parts = [];
  let last = 0;
  let m;
  let i = 0;
  while ((m = BOLD_RE.exec(segment)) !== null) {
    if (m.index > last) parts.push(<span key={`${baseKey}-t-${i++}`}>{segment.slice(last, m.index)}</span>);
    parts.push(<strong key={`${baseKey}-b-${i++}`} className="font-bold">{m[1]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < segment.length) parts.push(<span key={`${baseKey}-t-${i++}`}>{segment.slice(last)}</span>);
  return parts;
};
const RichText = ({ text }) => {
  if (!text) return null;
  const out = [];
  let last = 0;
  let m;
  let i = 0;
  while ((m = URL_RE.exec(text)) !== null) {
    if (m.index > last) {
      out.push(...renderRichSegment(text.slice(last, m.index), `s-${i++}`));
    }
    out.push(
      <a
        key={`u-${i++}`}
        href={m[0]}
        target="_blank"
        rel="noopener noreferrer"
        className="text-indigo-600 underline hover:text-indigo-800 break-all font-medium"
        data-testid="ted-link"
      >
        {m[0]}
      </a>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(...renderRichSegment(text.slice(last), `s-${i++}`));
  return <>{out}</>;
};


const TedRobot = ({ size = 56, className = '', animate = true }) => (
  <svg viewBox="0 0 120 120" className={className} width={size} height={size}>
    <rect x="20" y="15" width="80" height="70" rx="18" fill="#059669" />
    <rect x="25" y="20" width="70" height="60" rx="14" fill="#10b981" />
    <rect x="30" y="28" width="60" height="40" rx="8" fill="#0d1117" />
    <ellipse cx="48" cy="45" rx="7" ry="8" fill="#34d399">
      {animate && <animate attributeName="ry" values="8;1;8" dur="4s" repeatCount="indefinite" begin="2s" />}
    </ellipse>
    <ellipse cx="72" cy="45" rx="7" ry="8" fill="#34d399">
      {animate && <animate attributeName="ry" values="8;1;8" dur="4s" repeatCount="indefinite" begin="2s" />}
    </ellipse>
    <path d="M 45 56 Q 60 66 75 56" stroke="#34d399" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    <line x1="60" y1="15" x2="60" y2="5" stroke="#059669" strokeWidth="3" strokeLinecap="round" />
    <circle cx="60" cy="4" r="4" fill="#34d399">
      {animate && <animate attributeName="r" values="4;6;4" dur="2s" repeatCount="indefinite" />}
    </circle>
    <rect x="12" y="38" width="10" height="16" rx="4" fill="#059669" />
    <rect x="98" y="38" width="10" height="16" rx="4" fill="#059669" />
    <rect x="35" y="85" width="50" height="25" rx="8" fill="#059669" />
    <rect x="40" y="90" width="40" height="15" rx="5" fill="#10b981" />
    <circle cx="50" cy="97" r="3" fill="#34d399" />
    <circle cx="60" cy="97" r="3" fill="#6ee7b7" />
    <circle cx="70" cy="97" r="3" fill="#34d399" />
    <rect x="22" y="88" width="14" height="6" rx="3" fill="#059669" />
    <rect x="84" y="88" width="14" height="6" rx="3" fill="#059669" />
  </svg>
);

/* Typing effect component */
const TypingText = ({ text, onDone }) => {
  const [displayed, setDisplayed] = useState('');
  const idx = useRef(0);

  useEffect(() => {
    idx.current = 0;
    setDisplayed('');
    const speed = Math.max(12, 30 - text.length / 10);
    const timer = setInterval(() => {
      idx.current++;
      setDisplayed(text.slice(0, idx.current));
      if (idx.current >= text.length) {
        clearInterval(timer);
        onDone?.();
      }
    }, speed);
    return () => clearInterval(timer);
  }, [text]);

  return <><RichText text={displayed} /><span className="animate-pulse">|</span></>;
};

const TedChat = () => {
  const { language, localize } = useLanguage();
  const isAr = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [sessionId] = useState(() => `ted-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) inputRef.current.focus();
  }, [isOpen, isMinimized]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'ted',
        text: localize({ ar: 'مرحباً! أنا TED 👋\nكيف أقدر أساعدك؟', en: "Hi! I'm TED 👋\nHow can I help?" }),
        done: true
      }]);
    }
  }, [isOpen]);

  /**
   * Global "open TED" hook — any element on any page can fire
   *   window.dispatchEvent(new CustomEvent('lwt:open-ted'))
   * and the chat will pop up + unminimise. Used by the Hero TED card
   * on `/` so clicking the panel itself starts a conversation.
   */
  useEffect(() => {
    const open = () => { setIsOpen(true); setIsMinimized(false); };
    window.addEventListener('lwt:open-ted', open);
    return () => window.removeEventListener('lwt:open-ted', open);
  }, []);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    setMessages(prev => [...prev, { role: 'user', text: msg, done: true }]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ted/chat`, { message: msg, session_id: sessionId });
      setTyping(true);
      setMessages(prev => [...prev, { role: 'ted', text: res.data.reply, done: false }]);
    } catch {
      setMessages(prev => [...prev, { 
        role: 'ted', 
        text: localize({ ar: 'عذراً، حدث خطأ. حاول مرة أخرى.', en: 'Sorry, an error occurred. Try again.' }), 
        done: true 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const markDone = useCallback(() => {
    setTyping(false);
    setMessages(prev => prev.map((m, i) => i === prev.length - 1 ? { ...m, done: true } : m));
  }, []);

  /* ──── Voice Recording ──── */
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 1000) return; // Too short

        setTranscribing(true);
        try {
          const formData = new FormData();
          formData.append('audio', blob, 'voice.webm');
          const res = await axios.post(`${API}/ted/voice`, formData);
          const text = res.data.text?.trim();
          if (text) {
            setInput(text);
            // Auto-send after transcription
            sendMessage(text);
          }
        } catch {
          // Silently fail
        } finally {
          setTranscribing(false);
        }
      };

      mediaRecorder.start();
      setRecording(true);
    } catch {
      // Mic not available
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const toggleRecording = () => {
    if (recording) stopRecording();
    else startRecording();
  };

  return (
    <>
      {/* ─── Single Floating Action Button ─── */}
      {!isOpen && (
        <motion.button
          onClick={() => { setIsOpen(true); setIsMinimized(false); }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.5 }}
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.94 }}
          className={`
            fixed z-[60] group
            hidden md:block
            bottom-20 sm:bottom-6
            ${isAr ? 'left-4 sm:left-6' : 'right-4 sm:right-6'}
          `}
          data-testid="ted-chat-toggle"
          aria-label={localize({ ar: 'إخفاء المساعد TED', en: 'Open TED AI assistant' })}
        >
          {/* Soft pulsing halo */}
          {/* Lightweight CSS-only halo (no JS animation per frame) */}
          <span
            className="absolute inset-0 rounded-full bg-indigo-500/25 blur-xl animate-pulse motion-reduce:animate-none"
            aria-hidden
          />
          {/* Online indicator — simple dot, no ping animation */}
          <span className={`absolute top-1 ${isAr ? 'left-1' : 'right-1'} z-10 flex h-2.5 w-2.5`}>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-500 border-2 border-white" />
          </span>
          {/* Robot core */}
          <div className="relative w-11 h-11 sm:w-16 sm:h-16 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-full shadow-[0_8px_25px_rgba(79,70,229,0.45)] flex items-center justify-center hover:shadow-[0_12px_35px_rgba(79,70,229,0.6)] transition-shadow">
            <TedRobot size={26} animate={false} />
          </div>
          {/* Hover tooltip */}
          <span className={`absolute ${isAr ? 'right-full me-3' : 'left-full ms-3'} top-1/2 -translate-y-1/2 whitespace-nowrap bg-navy-800 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity hidden sm:block`}>
            {localize({ ar: 'TED — مساعد ذكي', en: 'TED — AI assistant' })}
            <span className={`absolute top-1/2 -translate-y-1/2 ${isAr ? '-right-1' : '-left-1'} w-2 h-2 bg-navy-800 rotate-45`} />
          </span>
        </motion.button>
      )}

      {/* Chat Window — compact corner panel on mobile (no longer fills width), anchored on desktop */}
      {isOpen && (
        <div
          className={`
            fixed z-[100] bg-white shadow-2xl border border-slate-200 overflow-hidden flex flex-col transition-all duration-300
            bottom-3 w-[78vw] max-w-[300px] rounded-2xl
            ${isAr ? 'left-3' : 'right-3'}
            sm:bottom-6 sm:w-[360px] sm:max-w-none sm:rounded-2xl
            ${isAr ? 'sm:left-6' : 'sm:right-6'}
          `}
          style={{
            animation: 'tedSlideUp 0.3s ease-out',
            // Mobile: cap to ~55% of viewport so the page underneath stays usable.
            // Desktop (sm: ≥640px): legacy 520px.
            maxHeight: isMinimized ? '52px' : 'min(55vh, 460px)',
          }}
          data-testid="ted-chat-window"
          dir={isAr ? 'rtl' : 'ltr'}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-violet-700 px-4 py-3 flex items-center gap-3 flex-shrink-0 cursor-pointer" onClick={() => isMinimized && setIsMinimized(false)}>
            <TedRobot size={32} animate={false} className="flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-bold text-sm">TED</h3>
              {!isMinimized && (
                <p className="text-indigo-200 text-[10px]">
                  {recording ? (localize({ ar: 'يستمع...', en: 'Listening...' })) : (localize({ ar: 'مساعدك الذكي', en: 'AI Assistant' }))}
                </p>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button 
                onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
                className="text-indigo-200 hover:text-white transition-colors p-1 rounded"
                data-testid="ted-minimize-btn"
              >
                {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                className="text-indigo-200 hover:text-white transition-colors p-1 rounded"
                data-testid="ted-close-chat-btn"
                aria-label={localize({ ar: 'إغلاق المحادثة', en: 'Close chat' })}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          {!isMinimized && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50 min-h-[200px] max-h-[260px] sm:min-h-[280px] sm:max-h-[360px]">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} items-end gap-2`}>
                    {msg.role === 'ted' && <TedRobot size={24} animate={false} className="flex-shrink-0 mb-1" />}
                    <div className={`max-w-[78%] px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${
                      msg.role === 'user'
                        ? 'bg-indigo-600 text-white rounded-2xl rounded-br-md'
                        : 'bg-white text-slate-700 rounded-2xl rounded-bl-md shadow-sm border border-slate-100'
                    }`}>
                      {msg.role === 'ted' && !msg.done
                        ? <TypingText text={msg.text} onDone={markDone} />
                        : msg.role === 'ted' ? <RichText text={msg.text} /> : msg.text
                      }
                    </div>
                  </div>
                ))}
                {(loading || transcribing) && (
                  <div className="flex items-end gap-2">
                    <TedRobot size={24} animate={false} className="flex-shrink-0 mb-1" />
                    <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-slate-100">
                      {transcribing ? (
                        <span className="text-xs text-indigo-600 font-medium">{localize({ ar: 'يحوّل الصوت...', en: 'Transcribing...' })}</span>
                      ) : (
                        <div className="flex gap-1.5">
                          <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                          <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                          <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Smart Starters — Outlook-style suggested prompts visible only at chat start.
                    Tuned for LWT funnel (Feb 2026): the three CEO-requested questions
                    bubble to the top in both AR & EN. */}
                {messages.length <= 1 && !loading && (
                  <div className="space-y-2 pt-1" data-testid="ted-quick-actions">
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                      {localize({ ar: 'اقتراحات سريعة', en: 'Quick prompts' })}
                    </p>
                    {[
                      {
                        icon: Calculator,
                        ar: 'أحتاج معلم رياضيات',
                        en: 'I need a Math tutor',
                        color: 'indigo',
                        testId: 'qa-need-math-tutor',
                      },
                      {
                        icon: HelpCircle,
                        ar: 'كم سعر الدروس؟',
                        en: 'How much do lessons cost?',
                        color: 'amber',
                        testId: 'qa-lesson-pricing',
                      },
                      {
                        icon: BookOpen,
                        ar: 'كيف أسجل كمعلم؟',
                        en: 'How do I sign up as a tutor?',
                        color: 'emerald',
                        testId: 'qa-become-tutor',
                      },
                      {
                        icon: Calendar,
                        ar: 'احجز لي درساً تجريبياً',
                        en: 'Schedule a trial lesson',
                        color: 'violet',
                        testId: 'qa-trial-lesson',
                      },
                      {
                        icon: Compass,
                        ar: 'أعطني جولة سريعة في المنصة',
                        en: 'Give me a quick platform tour',
                        color: 'rose',
                        testId: 'qa-platform-tour',
                      },
                    ].map((action, i) => {
                      const Icon = action.icon;
                      const colorMap = {
                        rose:    'bg-rose-50    hover:bg-rose-100    text-rose-700    border-rose-100    hover:border-rose-200',
                        indigo:  'bg-indigo-50  hover:bg-indigo-100  text-indigo-700  border-indigo-100  hover:border-indigo-200',
                        amber:   'bg-amber-50   hover:bg-amber-100   text-amber-700   border-amber-100   hover:border-amber-200',
                        emerald: 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-100 hover:border-emerald-200',
                        violet:  'bg-violet-50  hover:bg-violet-100  text-violet-700  border-violet-100  hover:border-violet-200',
                      };
                      const iconBg = {
                        rose:    'bg-rose-600',
                        indigo:  'bg-indigo-600',
                        amber:   'bg-amber-500',
                        emerald: 'bg-emerald-600',
                        violet:  'bg-violet-600',
                      };
                      return (
                        <motion.button
                          key={i}
                          type="button"
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: 0.6 + i * 0.1 }}
                          whileHover={{ x: isAr ? -2 : 2 }}
                          onClick={() => sendMessage(localize({ ar: action.ar, en: action.en }))}
                          className={`group w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-start text-[12px] font-semibold border transition-all ${colorMap[action.color]}`}
                          data-testid={action.testId}
                        >
                          <span className={`w-7 h-7 rounded-lg ${iconBg[action.color]} text-white flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                            <Icon className="w-3.5 h-3.5" />
                          </span>
                          <span className="flex-1">{localize({ ar: action.ar, en: action.en })}</span>
                          <span className="text-[10px] opacity-50 group-hover:opacity-100 transition-opacity">
                            {isAr ? '←' : '→'}
                          </span>
                        </motion.button>
                      );
                    })}
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 bg-white border-t border-slate-100 flex-shrink-0">
                <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
                  <input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={localize({ ar: 'اكتب أو اضغط المايك...', en: 'Type or tap mic...' })}
                    className="flex-1 bg-slate-50 rounded-xl px-4 py-2.5 text-sm border border-slate-200 focus:border-indigo-300 focus:ring-1 focus:ring-indigo-200 outline-none transition-all"
                    disabled={loading || recording || transcribing}
                    data-testid="ted-chat-input"
                  />
                  {/* Mic button */}
                  <button
                    type="button"
                    onClick={toggleRecording}
                    disabled={loading || transcribing}
                    className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all flex-shrink-0 ${
                      recording
                        ? 'bg-indigo-500 hover:bg-indigo-600 text-white shadow-md shadow-indigo-300'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-indigo-600'
                    }`}
                    data-testid="ted-voice-btn"
                  >
                    {recording ? (
                      <div className="relative flex items-center justify-center">
                        <span className="absolute w-8 h-8 bg-indigo-400/30 rounded-full animate-ping" />
                        <Mic className="w-4 h-4 relative z-10" />
                      </div>
                    ) : <Mic className="w-4 h-4" />}
                  </button>
                  {/* Send button */}
                  <button
                    type="submit"
                    disabled={loading || !input.trim() || recording || transcribing}
                    className="w-10 h-10 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded-xl flex items-center justify-center transition-colors flex-shrink-0"
                    data-testid="ted-chat-send"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </form>
              </div>
            </>
          )}
        </div>
      )}

      <style>{`
        @keyframes tedSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
};

export { TedRobot };
export default TedChat;
